import type { FormEvent } from 'react'

import { useEffect, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import Button from '@/components/ui/button'
import Field from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { CURRENT_USER } from '@/constants/companies'
import { useCrmStore } from '@/stores/crm-store-provider'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function InviteForm() {
    const showNotice = useCrmStore(state => state.showNotice)
    const [link, setLink] = useState('')
    const [copied, setCopied] = useState(false)
    const [emails, setEmails] = useState('')
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        setLink(window.location.origin)
    }, [])

    useEffect(() => {
        if (!copied) return

        const timer = window.setTimeout(() => setCopied(false), 2000)

        return () => window.clearTimeout(timer)
    }, [copied])

    async function copyLink() {
        try {
            await navigator.clipboard.writeText(link)
            setCopied(true)
        }
        catch {
            showNotice('error', "Copying is blocked in this browser. Select the link and copy it instead.")
        }
    }

    function compose(event: FormEvent<HTMLFormElement>) {
        event.preventDefault()

        const list = emails.split(/[\s,;]+/).map(item => item.trim()).filter(Boolean)

        if (list.length === 0 || list.some(item => !EMAIL_PATTERN.test(item))) {
            setError("Enter one or more valid email addresses, separated by commas.")

            return
        }

        const subject = "Join me on Sales CRM"
        const body = `Hi,\n\nI'm using Sales CRM to track our accounts and pipeline. You can open it here:\n${link}\n\n${CURRENT_USER.name}`

        window.location.href = `mailto:${list.join(',')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    }

    return (
        <form onSubmit={compose} noValidate className='flex flex-col'>
            <DialogHeader>
                <DialogTitle>Invite teammates</DialogTitle>
                <DialogDescription>
                    Share the CRM link with your team. Anyone with the link can open it.
                </DialogDescription>
            </DialogHeader>

            <div className='flex flex-col gap-5 px-6 py-5'>
                <Field label="CRM link" htmlFor='invite-link'>
                    <div className='flex gap-2'>
                        <Input id='invite-link' value={link} readOnly onFocus={event => event.currentTarget.select()} className='text-soft' />
                        <Button variant='secondary' size='sm' onClick={copyLink} disabled={!link} className='h-9 shrink-0 px-3'>
                            {copied ? <Check aria-hidden className='size-3.5' /> : <Copy aria-hidden className='size-3.5' />}
                            {copied ? "Copied" : "Copy"}
                        </Button>
                    </div>
                </Field>

                <Field
                    label="Email teammates"
                    htmlFor='invite-emails'
                    error={error ?? undefined}
                    hint="Opens your email app with a ready-to-send invite."
                >
                    <Input
                        id='invite-emails'
                        value={emails}
                        onChange={event => {
                            setEmails(event.target.value)
                            if (error) setError(null)
                        }}
                        placeholder="alex@company.com, sam@company.com"
                        autoComplete='off'
                        aria-invalid={error ? true : undefined}
                        aria-describedby={error ? 'invite-emails-error' : 'invite-emails-hint'}
                        className='aria-invalid:border-danger'
                    />
                </Field>
            </div>

            <DialogFooter>
                <DialogClose asChild>
                    <Button variant='subtle' size='sm'>
                        Close
                    </Button>
                </DialogClose>
                <Button variant='primary' size='sm' type='submit' disabled={!link}>
                    Compose invite
                </Button>
            </DialogFooter>
        </form>
    )
}

export default function InviteDialog() {
    const open = useCrmStore(state => state.dialog === 'invite')
    const closeDialog = useCrmStore(state => state.closeDialog)

    return (
        <Dialog open={open} onOpenChange={next => !next && closeDialog()}>
            <DialogContent className='max-w-[480px]'>
                <InviteForm />
            </DialogContent>
        </Dialog>
    )
}
